import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { PRODUCTS, getProduct, getOtherProducts } from "@/data/products";
import { PrivatePresentationCTA } from "@/components/PrivatePresentationCTA";
import { useLang, local } from "@/i18n/LanguageContext";

const Product = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const p = getProduct(slug);
  if (!p) return <Navigate to="/collection" replace />;

  const pr = t.product;
  const slugIndex = PRODUCTS.findIndex((x) => x.slug === p.slug);
  const others = getOtherProducts(slug).slice(0, 3);

  return (
    <div data-testid={`product-page-${p.slug}`}>
      {/* HERO ----------------------------------------------------------- */}
      <section className="pt-12 md:pt-20 pb-20 md:pb-28 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <Link to="/collection" className="font-eyebrow text-[hsl(var(--md-stone))] hover:text-[hsl(var(--md-ink))] inline-flex items-center gap-2 transition-colors">
            {pr.back}
          </Link>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-start">
            <div className="lg:col-span-7 lg:sticky lg:top-32">
              <div className="relative aspect-[5/6] lg:aspect-[4/5] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))]">
                <img
                  src={p.image}
                  alt={p.name}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>

            <div className="lg:col-span-5 lg:pt-8">
              <p className="font-eyebrow text-[hsl(var(--md-stone))]">
                — {pr.pieceLabel}&nbsp;{String(slugIndex + 1).padStart(2, "0")} · {local(p.capacityLabel, lang)}
              </p>
              <h1 className="display-hero mt-7 text-[hsl(var(--md-ink))] leading-[1.02]">
                {p.name}
              </h1>
              <p className="font-display italic text-[1.55rem] md:text-[1.85rem] mt-6 text-[hsl(var(--md-stone))] leading-snug max-w-[24ch]">
                {local(p.tagline, lang)}
              </p>

              <p className="editorial mt-12 text-[hsl(var(--md-ink))]">
                {local(p.description, lang)}
              </p>

              <div className="hairline mt-16" />

              {/* SPECS */}
              <div className="mt-12">
                <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-8">
                  {pr.specsTitle}
                </p>
                <ul className="space-y-5">
                  {local(p.specs, lang).map((spec, idx) => (
                    <li
                      key={idx}
                      data-testid={`spec-${p.slug}-${idx}`}
                      className="font-body text-[15px] text-[hsl(var(--md-ink))] flex gap-6 pb-5 border-b border-[hsl(var(--md-line))] last:border-b-0"
                    >
                      <span className="text-[hsl(var(--md-stone))] font-eyebrow pt-1 min-w-[28px]">
                        {pr.specOrdinal(idx)}
                      </span>
                      <span>{spec}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="hairline mt-14" />

              <div className="mt-12 flex items-end justify-between">
                <div>
                  <p className="font-eyebrow text-[hsl(var(--md-stone))]">
                    {pr.priceLabel}
                  </p>
                  <p className="font-eyebrow text-[hsl(var(--md-stone))] mt-1">
                    {pr.editionLabel}
                  </p>
                </div>
                <p className="display-1 text-[hsl(var(--md-ink))] leading-none">
                  {local(p.priceLabel, lang)}
                </p>
              </div>

              <div className="mt-14 flex flex-col sm:flex-row gap-5 sm:gap-8 sm:items-center">
                <Link
                  to={`/contact?piece=${p.name}`}
                  className="btn-primary"
                  data-testid={`product-cta-contact-${p.slug}`}
                >
                  {pr.ctaContact}
                </Link>
                <Link
                  to="/collection"
                  className="btn-ghost"
                  data-testid={`product-cta-collection-${p.slug}`}
                >
                  {pr.ctaCollection}
                  <ArrowUpRight size={14} strokeWidth={1.2} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER PIECES -------------------------------------------------- */}
      <section className="bg-[hsl(var(--md-bone))] border-y border-[hsl(var(--md-line))] py-24 md:py-32 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between flex-wrap gap-8">
            <div>
              <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-4">
                {pr.otherEyebrow}
              </p>
              <h2 className="display-2 text-[hsl(var(--md-ink))] max-w-[22ch]">
                {pr.otherTitle}
              </h2>
            </div>
            <Link to="/collection" className="btn-ghost" data-testid="product-link-collection">
              {pr.linkAll}
              <ArrowRight size={14} strokeWidth={1.2} />
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10">
            {others.map((o) => (
              <Link
                to={`/collection/${o.slug}`}
                key={o.slug}
                className="group block"
                data-testid={`product-other-${o.slug}`}
              >
                <div className="relative aspect-[4/5] bg-[hsl(var(--md-chalk))] border border-[hsl(var(--md-line))] overflow-hidden">
                  <img
                    src={o.image}
                    alt={o.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
                <div className="mt-5 flex items-baseline justify-between">
                  <h3 className="font-display text-[1.4rem] text-[hsl(var(--md-ink))]">{o.name}</h3>
                  <span className="font-eyebrow text-[hsl(var(--md-stone))]">{local(o.priceLabel, lang)}</span>
                </div>
                <p className="editorial-muted mt-1 text-[0.95rem]">{local(o.tagline, lang)}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <PrivatePresentationCTA />
    </div>
  );
};

export default Product;
